import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  TimePicker,
  Select,
  Button,
} from "antd";
import { CalendarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { sessionsService } from "../../services/api.service";
import logger from "../../utils/logger";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { sessionTypeOptions } from "../../constants/sessionTypes";
import locale from "antd/es/date-picker/locale/en_US";

// تفعيل plugin للتعامل مع التنسيقات المخصصة
dayjs.extend(customParseFormat);

const { Option } = Select;
const { TextArea } = Input;

const EditSessionModal = ({ sessionData, visible, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ date: null, time: null });

  useEffect(() => {
    if (visible && sessionData) {
      const sessionDate = sessionData.date ? dayjs(sessionData.date) : null;
      const sessionTime = sessionData.time
        ? dayjs(sessionData.time, "HH:mm:ss")
        : null;

      form.setFieldsValue({
        ...sessionData,
        date: sessionDate,
        time: sessionTime,
      });
    }
  }, [visible, sessionData, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // dayjs لا يدعم تغيير اللغة بهذه الطريقة، ولكن يمكن استخدام locale plugin إذا لزم الأمر

      // تنسيق البيانات للإرسال
      const formattedValues = {
        ...values,
        date: values.date ? values.date.format("YYYY-MM-DD") : null,
        // التأكد من تنسيق الوقت بشكل صحيح
        time: values.time ? dayjs(values.time).format("HH:mm:ss") : null,
        case_id: sessionData.case_id,
        client_id: sessionData.client_id,
      };

      // التحقق من وجود التاريخ والوقت
      if (!formattedValues.date || !formattedValues.time) {
        throw new Error("التاريخ والوقت مطلوبان");
      }

      await sessionsService.updateSession(sessionData.id, formattedValues);

      Swal.fire({
        title: "تم التحديث!",
        text: "تم تحديث بيانات الجلسة بنجاح",
        icon: "success",
        confirmButtonText: "حسناً",
      });

      if (onSuccess) onSuccess(formattedValues);
      onClose();
    } catch (error) {
      logger.error("Error updating session:", error);
      Swal.fire({
        title: "خطأ!",
        text:
          "فشل في تحديث بيانات الجلسة: " + (error.message || "خطأ غير معروف"),
        icon: "error",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoading(false);
      // dayjs لا يدعم تغيير اللغة بهذه الطريقة، ولكن يمكن استخدام locale plugin إذا لزم الأمر
    }
  };

  const handleDateChange = (date) => {
    logger.debug("Date changed:", date ? date.format("YYYY-MM-DD") : null);
    setFormData((prev) => ({ ...prev, date }));
    form.setFieldsValue({ date });
  };

  const handleTimeChange = (time) => {
    logger.debug("Time changed:", time ? time.format("HH:mm:ss") : null);
    setFormData((prev) => ({ ...prev, time }));
    form.setFieldsValue({ time });
  };

  return (
    <Modal
      title="تعديل بيانات الجلسة"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
      className="rtl-modal"
      bodyStyle={{ direction: "rtl", textAlign: "right" }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="rtl-form"
        dir="rtl"
      >
        <Form.Item
          name="title"
          label="عنوان الجلسة"
          rules={[{ required: false, message: "يرجى إدخال عنوان الجلسة" }]}
        >
          <Input className="rtl-input" placeholder="أدخل عنوان الجلسة" />
        </Form.Item>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            name="session_type"
            label="نوع الجلسة"
            rules={[{ required: true, message: "يرجى اختيار نوع الجلسة" }]}
          >
            <Select placeholder="اختر نوع الجلسة" className="rtl-select">
              {sessionTypeOptions.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="حالة الجلسة"
            rules={[{ required: true, message: "يرجى اختيار حالة الجلسة" }]}
          >
            <Select placeholder="اختر حالة الجلسة" className="rtl-select">
              <Option value="منعقدة">منعقدة</Option>
              <Option value="مؤجلة">مؤجلة</Option>
              <Option value="ملغية">ملغية</Option>
              <Option value="مكتملة">مكتملة</Option>
            </Select>
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            name="date"
            label="تاريخ الجلسة"
            rules={[{ required: true, message: "يرجى اختيار تاريخ الجلسة" }]}
          >
            <DatePicker
              className="text-right h-11 w-full"
              format="YYYY/MM/DD"
              locale={locale}
              placeholder="اختر تاريخ الجلسة"
              suffixIcon={<CalendarOutlined className="text-primary" />}
              value={formData.date}
              onChange={handleDateChange}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="time"
            label="وقت الجلسة"
            rules={[{ required: true, message: "يرجى اختيار وقت الجلسة" }]}
          >
            <TimePicker
              className="text-right h-11 w-full"
              format="HH:mm"
              placeholder="اختر وقت الجلسة"
              value={formData.time}
              onChange={handleTimeChange}
              size="large"
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="court" label="المحكمة">
            <Input className="rtl-input" placeholder="أدخل اسم المحكمة" />
          </Form.Item>

          <Form.Item name="room" label="القاعة">
            <Input className="rtl-input" placeholder="أدخل رقم القاعة" />
          </Form.Item>
        </div>

        <Form.Item name="judge" label="القاضي">
          <Input className="rtl-input" placeholder="أدخل اسم القاضي" />
        </Form.Item>

        <Form.Item name="decisions" label="القرارات">
          <TextArea
            rows={4}
            placeholder="أدخل قرارات الجلسة"
            className="rtl-textarea"
          />
        </Form.Item>

        <Form.Item name="notes" label="ملاحظات">
          <TextArea
            rows={4}
            placeholder="أدخل ملاحظات إضافية"
            className="rtl-textarea"
          />
        </Form.Item>

        <div className="flex justify-end mt-4">
          <Button onClick={onClose} className="ml-2">
            إلغاء
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            حفظ التعديلات
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditSessionModal;
